import React, { useEffect, useRef, useState } from 'react';
import {
    BaseSystemPlugin,
    useActiveFrameId,
    useFramework,
    useIdentity,
    useScale,
    useTranslateXY,
    useUnit,
    useViewSize,
} from '@/framework';
import { getLayerInfoWithRotate } from '@/utils/layer-kit';
import { ILayerProps } from '@/version-types';
import { RULER_BAR_SIZE } from '../constants';
import { drawHorizontalRuler, drawVerticalRuler } from '../operations/draw-rules';
import { getActiveLayerRulerRect } from '../operations/get-active-layer-ruler-rect';
import { setRulerBarVisibility } from '../operations/set-ruler-bar-visibility';
import { syncRulerViewport, type IRulerViewportState } from '../operations/sync-ruler-viewport';
import { RulerBar as RulerBarController } from '../ruler-bar';
import { getRulerState, useRulerState } from '../states/ruler-state';
import type { RulerUnit } from '../types';
import UnitButton from './unit-button';
import './index.less';
import { getLocaleStore } from '@/i18n';

const RulerBar: React.FC = () => {
    const scale = useScale();
    const identity = useIdentity();
    const framework = useFramework();
    const { edgelessCanvas, events } = framework.service;
    const { width: viewWidth, height: viewHeight } = useViewSize();
    const translateXY = useTranslateXY();
    const showRulerBar = useRulerState()((state) => state.showRulerBar);
    const rulerUnit = useRulerState()((state) => state.rulerUnit);
    const setRulerUnit = useRulerState()((state) => state.setRulerUnit);
    const stageUnit = useUnit();
    const activeFrameId = useActiveFrameId();
    const horizontalRulerRef = useRef<HTMLCanvasElement>(null);
    const verticalRulerRef = useRef<HTMLCanvasElement>(null);
    const rulerViewportStateRef = useRef<IRulerViewportState>({ isPaddingApplied: false });
    const [_, refresh] = useState(0);

    const [activeLayer, setActiveInfo] = useState<ILayerProps | null>(framework.service.frame.getActivePickLayer());
    // 监听激活图层数据更新、激活图层切换
    useEffect(() => {
        const handler = framework.service.frame.onActiveLayerDataUpdate((activeLayer, pickLayer) => {
            setActiveInfo(pickLayer || activeLayer);
        });
        return () => events.off(handler);
    }, []);

    useEffect(() => {
        const handler = framework.service.frame.onResize((frameId) => {
            if (frameId === framework.service.frame.getActiveFrameId()) {
                refresh((v) => v + 1);
            }
        });
        return () => framework.service.events.off(handler);
    }, []);

    useEffect(() => {
        const rulerBar = framework.service.registration.getInstance<RulerBarController>('RulerBar');
        const syncViewport = (show: boolean) => {
            syncRulerViewport(edgelessCanvas, show, rulerViewportStateRef.current);
        };
        const unsubscribe = rulerBar.onShowRulerBarChanged(syncViewport);

        syncViewport(rulerBar.getShowRulerBar());

        return () => {
            unsubscribe();
            syncViewport(false);
        };
    }, [edgelessCanvas, framework.service.registration]);

    // 键盘事件(显示/隐藏标尺)
    useEffect(() => {
        const { $t } = getLocaleStore();
        framework.service.hotkey.register({
            name: $t((v) => v.Text_ToggleRulerBar),
            win: 'Shift r',
            mac: '⇧ r',
            key: 'toggleRulerBar',
            // 标尺属于视图设置，消除笔、裁剪等禁用图层拾取的模式下也应允许切换
            ignoreDisablePick: true,
            showInKeyboardShortcut: {
                edgeless: false,
            },
            processors: [
                {
                    press: ['Shift+r'],
                    fn: () => {
                        const newShowRulerBar = getRulerState(identity).showRulerBar;
                        setRulerBarVisibility(identity, !newShowRulerBar);
                    },
                },
            ],
        });
    }, []);

    // 单向同步右侧面板的单位
    useEffect(() => {
        setRulerUnit(stageUnit as RulerUnit);
    }, [stageUnit]);

    useEffect(() => {
        if (showRulerBar) {
            drawHorizontalRuler({
                scale,
                rulerUnit,
                paddingLeft: edgelessCanvas.translateX,
                scrollLeft: 0,
                viewWidth,
                stageSize: framework.service.frame.getSize(),
                horizontalRulerRef,
            });
        }
    }, [scale, rulerUnit, viewWidth, translateXY.translateX, showRulerBar, activeFrameId, _]);

    useEffect(() => {
        if (showRulerBar) {
            drawVerticalRuler({
                scale,
                rulerUnit,
                paddingTop: edgelessCanvas.translateY,
                scrollTop: 0,
                viewHeight,
                stageSize: framework.service.frame.getSize(),
                verticalRulerRef,
            });
        }
    }, [scale, rulerUnit, viewHeight, translateXY.translateY, showRulerBar, activeFrameId, _]);

    // 阻止右键菜单
    const preventContextMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    if (!showRulerBar) {
        return null;
    }

    const { width, height, left, top } = activeLayer
        ? getLayerInfoWithRotate(activeLayer)
        : { width: 0, height: 0, left: 0, top: 0 };
    const activeLayerRulerRect = getActiveLayerRulerRect({
        width,
        height,
        left,
        top,
        frameOffsetTop: activeLayer ? framework.service.transform.calcLayerOffsetTop(activeLayer) : 0,
        scale,
        translateX: edgelessCanvas.translateX,
        translateY: edgelessCanvas.translateY,
    });

    return (
        <BaseSystemPlugin name="ruler-bar">
            <div className="l-ruler-unix" data-operate-type="ruler-bar-unit" onContextMenu={preventContextMenu}>
                <UnitButton />
            </div>

            <div className="l-horizontal-ruler__container" onContextMenu={preventContextMenu}>
                {activeLayer && (
                    <div
                        className="l-active-shadow l-active-horizontal-shadow"
                        style={{
                            left: activeLayerRulerRect.left,
                            width: activeLayerRulerRect.width,
                        }}
                    />
                )}
                <canvas
                    ref={horizontalRulerRef}
                    data-operate-type="ruler-horizontal"
                    className="l-ruler-bar__canvas horizontal-ruler"
                    style={{ width: viewWidth - RULER_BAR_SIZE, height: RULER_BAR_SIZE }}
                />
            </div>

            <div className="l-vertical-ruler__container" onContextMenu={preventContextMenu}>
                {activeLayer && (
                    <div
                        className="l-active-shadow l-active-vertical-shadow"
                        style={{
                            top: activeLayerRulerRect.top,
                            height: activeLayerRulerRect.height,
                        }}
                    />
                )}
                <canvas
                    ref={verticalRulerRef}
                    data-operate-type="ruler-vertical"
                    className="l-ruler-bar__canvas vertical-ruler"
                    style={{ width: RULER_BAR_SIZE, height: viewHeight - RULER_BAR_SIZE }}
                />
            </div>
        </BaseSystemPlugin>
    );
};

export default React.memo(RulerBar);
